import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertOctagon, CheckCircle2, Eraser, Info, Loader2, TriangleAlert, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { useI18n } from '@/lib/i18n';
import { listItems } from '@/lib/items-api';
import { ROZMIAR_STRONY_TOWAROW, podsumowanieListy } from '@/lib/lista-towarow';
import {
  podgladPrzypisania,
  przypiszTowar,
  ustawRegulyKolumny,
  type Compartment,
  type PlanSkutkow,
  type Planogram as PlanogramData,
  type PrzypiszPayload,
} from '@/lib/planogram-api';
import { cn } from '@/lib/utils';

/**
 * ⚠️ WYEKSPORTOWANE 09.09.2026 dla okna podmiany indeksu — ten sam wybór towaru w dwu miejscach
 * musi być tym samym komponentem. Szukajka niesie już lekcję o „całej wyspie w jednym pobraniu"
 * (`lib/lista-towarow.ts`); druga kopia zaczynałaby od pierwszych dwudziestu pozycji.
 */
export type Towar = { id: string; itemNumber: string; name: string; packetSize: number | null };
type Kolumna = PlanogramData['drawers'][number];

/**
 * Górna granica jednej operacji — MUSI ZGADZAĆ SIĘ z `PrzypiszSchema` w API (`.max(200)`).
 * Powielona świadomie, żeby pole nie pozwalało wysłać czegoś, o czym z góry wiadomo, że wróci
 * błędem; API i tak pilnuje swojego, bo limit chroni bazę klienta, a nie ekran.
 */
const LIMIT_PRZEGRODEK = 200;

/**
 * PANEL EDYCJI — JEDYNE miejsce, w którym cokolwiek się zmienia.
 *
 * ⚠️ TO JEST ROZSTRZYGNIĘCIE, NIE UKŁAD GRAFICZNY. W starym Vendi zmiany robiło się „raz tu, raz
 * tam" — część w oknie kolumny, część w pasku pod planogramem — i użytkownik zgłosił to wprost
 * jako niekonsekwencję. U nas obowiązuje jedna zasada: **dół odpowiada wyłącznie za zaznaczanie,
 * a wszystko, co zmienia dane, dzieje się tutaj.** Dokładanie tu przycisku zaznaczania albo tam
 * pola edycji łamie tę zasadę i ma być odrzucane w recenzji.
 *
 * Panel jest `fixed` względem okna, bo siatka bębna jedzie w poziomie na 54 kolumny: przy
 * przewijaniu w bok opis dopisany pod siatką znika z ekranu razem z komórką, o którą chodzi.
 */
export function PanelEdycji({
  groupId,
  machineId,
  zaznaczone,
  gdzieJest,
  wieleBebnow,
  onZamknij,
}: {
  groupId: string;
  machineId: string;
  zaznaczone: ReadonlySet<string>;
  gdzieJest: Map<string, { kolumna: Kolumna; c: Compartment }>;
  /** Numer bębna pokazujemy TYLKO przy automacie z dostawką — inaczej to liczba bez treści. */
  wieleBebnow: boolean;
  onZamknij: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();

  const [szukaj, setSzukaj] = useState('');
  const [wybrany, setWybrany] = useState<Towar | null>(null);
  const [maks, setMaks] = useState('');
  const [wyd, setWyd] = useState('');
  /**
   * ⚠️ ZAZNACZENIE ZMIENIA WARTOŚCI POCZĄTKOWE FORMULARZA, więc trzymamy klucz tego, co
   * ostatnio wypełniliśmy. Bez tego każdy przerysunek panelu nadpisywałby to, co człowiek
   * zdążył wpisać, a przy wpisywaniu liczby przerysunków są dziesiątki.
   */
  const wypelnioneDla = useRef<string | null>(null);
  const [potwierdzenie, setPotwierdzenie] = useState<{ skutki: PlanSkutkow; payload: PrzypiszPayload } | null>(null);

  const ids = useMemo(() => [...zaznaczone], [zaznaczone]);

  const opis = useMemo(() => {
    const trafienia = ids.map((id) => gdzieJest.get(id)).filter((x) => x !== undefined);
    const zTowarem = trafienia.filter((x) => x.c.item !== null).length;
    const kolumny = [...new Set(trafienia.map((x) => x.kolumna.drawerNo))].sort((a, b) => a - b);
    /**
     * ⚠️ REGUŁY POKAZUJEMY, GDY ZAZNACZENIE MIEŚCI SIĘ W JEDNEJ KOLUMNIE — niekoniecznie gdy
     * obejmuje ją całą. Poprzednia wersja wymagała pełnej kolumny i użytkownik zgłosił, że
     * „w dalszym ciągu nie ma checkboxa". Reguła i tak dotyczy CAŁEJ kolumny niezależnie od tego,
     * ile komórek jest zaznaczonych — więc warunek „cała kolumna" niczego nie chronił, tylko
     * chował przycisk. Działa jednakowo na BiPoincie i na DynaBoksie; różnicę w tym, gdzie
     * reguła mieszka i co obejmuje, rozstrzyga API.
     */
    const kolumna = kolumny.length === 1 ? (trafienia[0]?.kolumna ?? null) : null;
    const czesciowe = kolumny.filter((nr) => {
      const kol = trafienia.find((x) => x.kolumna.drawerNo === nr)?.kolumna;
      if (kol === undefined || !kol.uniformContent) return false;
      return kol.compartments.some((c) => !zaznaczone.has(c.id));
    });
    const jedna = trafienia.length === 1 ? trafienia[0]! : null;
    return { zTowarem, puste: trafienia.length - zTowarem, kolumny, kolumna, czesciowe, jedna };
  }, [ids, gdzieJest, zaznaczone]);

  /**
   * PODPOWIEDŹ LIMITÓW.
   *
   * ⚠️ DWA RÓŻNE ŹRÓDŁA I KOLEJNOŚĆ MA ZNACZENIE:
   *  1. Gdy zaznaczone przegródki MAJĄ JUŻ jeden wspólny towar — bierzemy ich obecne limity
   *     i podstawiamy sam towar. To jest odpowiedź na „nie można edytować maks/wyd na towarze,
   *     który już jest dodany, a w SPS można": wystarczy zmienić liczbę i wysłać, a plan złoży
   *     wtedy `P` zamiast `D`+`I`, więc stan magazynowy zostaje.
   *  2. Gdy człowiek wybiera towar z wyszukiwarki — bierzemy `PACKETSIZE`, czyli po ile sztuk
   *     jest pakowany. Towar pakowany po dwie ustawia się na 2/2 sam.
   *
   * Podpowiedź NIE jest przymusem — oba pola zostają edytowalne, a puste znaczy „bez zmian".
   */
  useEffect(() => {
    const klucz = ids.join(',');
    if (wypelnioneDla.current === klucz) return;
    wypelnioneDla.current = klucz;

    const trafienia = ids.map((id) => gdzieJest.get(id)).filter((x) => x !== undefined);
    const towary = new Set(trafienia.map((x) => x.c.item?.id ?? null));
    const wspolny = towary.size === 1 ? (trafienia[0]?.c ?? null) : null;

    if (wspolny?.item != null) {
      setWybrany({
        id: wspolny.item.id,
        itemNumber: wspolny.item.itemNumber,
        name: wspolny.item.name,
        packetSize: null,
      });
      setMaks(wspolny.qtyMax === null ? '' : String(wspolny.qtyMax));
      setWyd(wspolny.qtyDisp === null ? '' : String(wspolny.qtyDisp));
    } else {
      setWybrany(null);
      setMaks('');
      setWyd('');
    }
  }, [ids, gdzieJest]);

  /** Wybór z wyszukiwarki podstawia limity z opakowania — patrz opis wyżej. */
  const wybierzTowar = (towar: Towar | null) => {
    setWybrany(towar);
    if (towar !== null && towar.packetSize !== null && towar.packetSize > 0) {
      setMaks(String(towar.packetSize));
      setWyd(String(towar.packetSize));
    }
  };

  const towary = useQuery({
    queryKey: ['items', 'planogram-picker', groupId, szukaj],
    queryFn: () =>
      listItems({
        groupId,
        ...(szukaj.trim() === '' ? {} : { search: szukaj.trim() }),
        status: 'active',
        duplicateSupplierPart: false,
        page: 1,
        /**
         * ⚠️ CAŁA WYSPA W JEDNYM POBRANIU, nie 20 pozycji. Przy `20` lista kończyła się bez słowa
         * na „REKAWICE SRUTOWNICZE" i nie było widać, że dalej są jeszcze towary na S — patrz
         * `lib/lista-towarow.ts`. Licznik pod listą pilnuje przypadku, gdy i to nie wystarczy.
         */
        pageSize: ROZMIAR_STRONY_TOWAROW,
        sort: 'name',
        dir: 'asc',
      }),
    enabled: Boolean(groupId),
  });

  const liczba = (s: string) => (s.trim() === '' ? undefined : Number(s));

  const zbudujPayload = (itemId: string | null): PrzypiszPayload => {
    const qtyMax = liczba(maks);
    const qtyDisp = liczba(wyd);
    return {
      groupId,
      machineId,
      compartmentIds: ids,
      itemId,
      ...(qtyMax === undefined ? {} : { qtyMax }),
      ...(qtyDisp === undefined ? {} : { qtyDisp }),
    };
  };

  const podglad = useMutation({
    mutationFn: (payload: PrzypiszPayload) => podgladPrzypisania(payload).then((skutki) => ({ skutki, payload })),
    onSuccess: setPotwierdzenie,
    onError: (e: Error) => toast.error(e.message),
  });

  const zapisz = useMutation({
    mutationFn: przypiszTowar,
    onSuccess: async (r) => {
      setPotwierdzenie(null);
      onZamknij();
      toast.success(
        t('planogram.assignDone').replace('{n}', String(r.skutki.compartments)) +
          (r.skutki.stockLost > 0 ? ` · ${t('planogram.stockLost').replace('{n}', String(r.skutki.stockLost))}` : ''),
      );
      await qc.invalidateQueries({ queryKey: ['planogram'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const reguly = useMutation({
    mutationFn: ustawRegulyKolumny,
    onSuccess: async (r) => {
      toast.success(r.zasiegPelnegoWydania === 'beben' ? t('planogram.rulesDoneDrum') : t('planogram.rulesDone'));
      await qc.invalidateQueries({ queryKey: ['planogram'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const zajete = podglad.isPending || zapisz.isPending;
  const zaDuzo = ids.length > LIMIT_PRZEGRODEK;
  const kol = opis.kolumna;

  return (
    <>
      <aside
        role="complementary"
        aria-label={t('planogram.detailsTitle')}
        className="fixed right-0 top-0 z-40 flex h-dvh w-[min(23rem,92vw)] flex-col border-l border-border bg-card shadow-2xl"
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold tracking-tight">
              {opis.jedna !== null
                ? [
                    ...(wieleBebnow ? [t('planogram.drum').replace('{n}', String(opis.jedna.kolumna.picNumber))] : []),
                    `${t('planogram.column')} ${opis.jedna.kolumna.drawerNo}`,
                    `${t('planogram.row')} ${opis.jedna.c.rowNo}`,
                  ].join(', ')
                : t('planogram.selectedCount').replace('{n}', String(ids.length))}
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {opis.jedna !== null
                ? (opis.jedna.c.item?.name ?? t('planogram.emptySlot'))
                : t('planogram.selectedBreakdown')
                    .replace('{withItem}', String(opis.zTowarem))
                    .replace('{empty}', String(opis.puste))}
            </p>
          </div>
          <Button variant="ghost" size="sm" aria-label={t('common.close')} onClick={onZamknij}>
            <X />
          </Button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {/* SZCZEGÓŁY jednej przegródki — przy wielu opis jednej z nich byłby mylący. */}
          {opis.jedna !== null && opis.jedna.c.item !== null && (
            <dl className="grid gap-y-1.5 text-sm">
              {(
                [
                  [t('planogram.itemNumber'), opis.jedna.c.item.itemNumber],
                  [t('planogram.group'), opis.jedna.c.item.groupName ?? '—'],
                  [t('planogram.stock'), `${opis.jedna.c.qtyCurrent}/${opis.jedna.c.qtyMax} ${opis.jedna.c.item.unit}`],
                  [t('planogram.dispense'), String(opis.jedna.c.qtyDisp ?? '—')],
                  [t('machines.fill'), opis.jedna.c.fillPercent === null ? '—' : `${opis.jedna.c.fillPercent}%`],
                  /**
                   * WSPÓŁRZĘDNE SPS wprost, bo nazwy są odwrotne do fizycznych: kolumna to
                   * `DRAWERNUMBER`, wiersz to `COMPARTMENTNUMBER`. Bez tego rozmowa z serwisem
                   * o „szufladzie 7" wskazuje dwie różne rzeczy po dwóch stronach słuchawki.
                   */
                  [
                    t('planogram.spsCoords'),
                    `${wieleBebnow ? `PIC ${opis.jedna.kolumna.picNumber} / ` : ''}DRAWER ${opis.jedna.kolumna.drawerNo} / COMP ${opis.jedna.c.compartmentNo}`,
                  ],
                ] as const
              ).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 border-b border-border py-1 last:border-0">
                  <dt className="shrink-0 text-muted-foreground">{k}</dt>
                  <dd className="text-right font-medium">{v}</dd>
                </div>
              ))}
            </dl>
          )}

          {/*
            REGUŁY KOLUMNY — zawsze, gdy zaznaczenie mieści się w jednej kolumnie. To jest to,
            czego brakowało: ekran ostrzegał o regule jednolitej zawartości, a nie dawał czym
            jej wyłączyć.
          */}
          {kol !== null && (
            <section className="space-y-2 rounded-md border border-border p-3">
              <h3 className="text-xs font-semibold text-muted-foreground">
                {t('planogram.columnRules').replace('{n}', String(kol.drawerNo))}
              </h3>

              <PoleReguly
                zaznaczone={kol.uniformContent}
                etykieta={t('planogram.uniformLabel')}
                opis={t('planogram.uniformToggleHint')}
                zajete={reguly.isPending}
                onZmien={(v) =>
                  reguly.mutate({ groupId, machineId, picNumber: kol.picNumber, columnNo: kol.drawerNo, uniformContent: v })
                }
              />
              {!kol.uniformKnown && <p className="text-[11px] text-muted-foreground">{t('planogram.uniformUnknown')}</p>}

              <PoleReguly
                zaznaczone={kol.enforceFullEmpty}
                etykieta={t('planogram.enforceLabel')}
                opis={kol.enforceFullEmptyScope === 'beben' ? t('planogram.enforceHintDrum') : t('planogram.enforceHint')}
                zajete={reguly.isPending}
                onZmien={(v) =>
                  reguly.mutate({
                    groupId,
                    machineId,
                    picNumber: kol.picNumber,
                    columnNo: kol.drawerNo,
                    enforceFullEmpty: v,
                  })
                }
              />
              {/*
                ⚠️ ZASIĘG PRZY POLU, nie w dokumentacji. `ENFORCEFULLEMPTYRULE` stoi w `Drawers`,
                a DynaBox ma tam JEDEN wiersz na cały bęben — więc ten przełącznik zmienia regułę
                wszystkim kolumnom tego bębna naraz.
              */}
              {kol.enforceFullEmptyScope === 'beben' && (
                <p className="flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-400">
                  <TriangleAlert className="mt-px size-3 shrink-0" />
                  {t('planogram.enforceScopeDrum')}
                </p>
              )}
            </section>
          )}

          {opis.czesciowe.length > 0 && (
            <p className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400">
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
              {t('planogram.uniformPartialWarning').replace('{list}', opis.czesciowe.join(', '))}
            </p>
          )}

          {zaDuzo && (
            <p className="flex items-start gap-2 text-xs text-destructive">
              <AlertOctagon className="mt-0.5 size-3.5 shrink-0" />
              {t('planogram.tooManySelected').replace('{limit}', String(LIMIT_PRZEGRODEK))}
            </p>
          )}

          <section className="space-y-3 rounded-md border border-border p-3">
            <h3 className="text-xs font-semibold text-muted-foreground">
              {t('planogram.editSection').replace('{n}', String(ids.length))}
            </h3>

            <TowarPicker
              szukaj={szukaj}
              onSzukaj={setSzukaj}
              wybrany={wybrany}
              onWybierz={wybierzTowar}
              wyniki={towary.data?.rows ?? []}
              razem={towary.data?.total ?? 0}
              ladowanie={towary.isPending}
            />

            <div className="flex gap-2">
              <label className="flex flex-1 flex-col gap-1 text-xs text-muted-foreground">
                {t('planogram.max')}
                <Input
                  type="number"
                  min={1}
                  value={maks}
                  onChange={(e) => setMaks(e.target.value)}
                  placeholder={t('planogram.keepCurrent')}
                  className="h-9"
                  title={t('planogram.maxHint')}
                />
              </label>
              {/*
                ILOŚĆ WYDAWANA obok pojemności — to ona odpowiada na „paczka po pięć sztuk,
                człowiek wyciąga całą". Puste = tyle, ile pojemność, i tak jest ustawione
                wszystko na obu automatach DBW (1329 wierszy, QTYDISP = QTYMAX bez wyjątku).
              */}
              <label className="flex flex-1 flex-col gap-1 text-xs text-muted-foreground">
                {t('planogram.dispense')}
                <Input
                  type="number"
                  min={0}
                  value={wyd}
                  onChange={(e) => setWyd(e.target.value)}
                  placeholder={t('planogram.sameAsMax')}
                  className="h-9"
                  title={t('planogram.dispenseHint')}
                />
              </label>
            </div>

            <div className="flex gap-2">
              <Button
                size="sm"
                className="flex-1"
                disabled={zajete || zaDuzo || wybrany === null || ids.length === 0}
                onClick={() => podglad.mutate(zbudujPayload(wybrany?.id ?? null))}
              >
                {zajete ? <Loader2 className="animate-spin" /> : <CheckCircle2 />}
                {t('planogram.assignSelected')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={zajete || zaDuzo || ids.length === 0}
                onClick={() => podglad.mutate(zbudujPayload(null))}
                title={t('planogram.clearHint')}
              >
                <Eraser />
                {t('planogram.clearSelected')}
              </Button>
            </div>
          </section>
        </div>
      </aside>

      <Dialog open={potwierdzenie !== null} onOpenChange={(o) => !o && setPotwierdzenie(null)}>
        {potwierdzenie && (
          <DialogContent
            title={potwierdzenie.payload.itemId === null ? t('planogram.confirmClear') : t('planogram.confirmAssign')}
            description={
              potwierdzenie.payload.itemId === null
                ? t('planogram.confirmClearDesc').replace('{n}', String(potwierdzenie.skutki.compartments))
                : t('planogram.confirmAssignDesc')
                    .replace('{item}', wybrany?.name ?? '')
                    .replace('{n}', String(potwierdzenie.skutki.compartments))
            }
          >
            <div className="space-y-3 text-sm">
              <dl className="space-y-1.5">
                {(
                  [
                    [t('planogram.effectEmptyNow'), potwierdzenie.skutki.emptyNow],
                    [t('planogram.effectSameItem'), potwierdzenie.skutki.sameItem],
                    [t('planogram.effectReplaced'), potwierdzenie.skutki.replaced],
                  ] as const
                ).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">{k}</dt>
                    <dd className="font-medium tabular-nums">{v}</dd>
                  </div>
                ))}
              </dl>

              {/* UTRATA STANU JEST GŁÓWNYM KOMUNIKATEM TEGO OKNA, a nie przypisem. */}
              {potwierdzenie.skutki.stockLost > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-destructive">
                  <AlertOctagon className="mt-0.5 size-4 shrink-0" />
                  <span>{t('planogram.confirmStockLoss').replace('{n}', String(potwierdzenie.skutki.stockLost))}</span>
                </div>
              )}

              {potwierdzenie.skutki.uniformColumnsPartial.length > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-amber-800 dark:text-amber-300">
                  <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                  <span>
                    {t('planogram.uniformPartialWarning').replace(
                      '{list}',
                      potwierdzenie.skutki.uniformColumnsPartial.join(', '),
                    )}
                  </span>
                </div>
              )}

              {potwierdzenie.skutki.uniformRulesDropped.length > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-amber-800 dark:text-amber-300">
                  <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                  <span>
                    {t('planogram.uniformDropped').replace(
                      '{list}',
                      potwierdzenie.skutki.uniformRulesDropped.join(', '),
                    )}
                  </span>
                </div>
              )}

              {potwierdzenie.skutki.addsMachineItem && (
                <p className="flex items-start gap-2 text-muted-foreground">
                  <Info className="mt-0.5 size-4 shrink-0" />
                  {t('planogram.addsMachineItem')}
                </p>
              )}

              <p className="text-xs text-muted-foreground">{t('planogram.queuedNotice')}</p>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPotwierdzenie(null)} disabled={zapisz.isPending}>
                {t('common.cancel')}
              </Button>
              <Button
                variant={potwierdzenie.skutki.stockLost > 0 ? 'destructive' : 'default'}
                disabled={zapisz.isPending}
                onClick={() => zapisz.mutate(potwierdzenie.payload)}
              >
                {zapisz.isPending && <Loader2 className="animate-spin" />}
                {t('planogram.confirmSend')}
              </Button>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}

/** Przełącznik reguły — pole wyboru z opisem, bo sama etykieta nie mówi, co reguła robi. */
function PoleReguly({
  zaznaczone,
  etykieta,
  opis,
  zajete,
  onZmien,
}: {
  zaznaczone: boolean;
  etykieta: string;
  opis: string;
  zajete: boolean;
  onZmien: (v: boolean) => void;
}) {
  return (
    <label className={cn('flex cursor-pointer items-start gap-2 text-xs', zajete && 'pointer-events-none opacity-60')}>
      <input
        type="checkbox"
        checked={zaznaczone}
        disabled={zajete}
        onChange={(e) => onZmien(e.target.checked)}
        className="mt-0.5 size-4 shrink-0 accent-primary"
      />
      <span>
        <span className="font-medium text-foreground">{etykieta}</span>
        <span className="mt-0.5 block text-muted-foreground">{opis}</span>
      </span>
    </label>
  );
}

/** Wyszukiwarka towaru — lista rozwija się dopiero po wpisaniu, żeby nie zasłaniać panelu. */
export function TowarPicker({
  szukaj,
  onSzukaj,
  wybrany,
  onWybierz,
  wyniki,
  razem,
  ladowanie,
  pozycjaPusta,
}: {
  szukaj: string;
  onSzukaj: (s: string) => void;
  wybrany: Towar | null;
  onWybierz: (t: Towar | null) => void;
  wyniki: Towar[];
  /** Ile towarów pasuje RAZEM — do licznika pod listą. Patrz `lib/lista-towarow.ts`. */
  razem: number;
  ladowanie: boolean;
  /**
   * PIERWSZA POZYCJA LISTY, KTÓRA NIE JEST TOWAREM — np. „przegródka pusta".
   *
   * ⚠️⚠️ DOSZŁO 09.09.2026 dla okna wymiany indeksu: „dodajmy jeszcze opcję, by po prostu
   * wyczyścić na puste przegrody — może to być jako pierwsza rzecz na liście".
   *
   * ⚠️⚠️ OPCJONALNE, ŻEBY PANEL EDYCJI ZOSTAł NIETKNIĘTY. Tam opróżnianie ma WŁASNY przycisk,
   * a druga droga do tej samej rzeczy w jednym panelu to dwie odpowiedzi na jedno pytanie.
   * Bez tego propsa komponent zachowuje się dokładnie jak dotąd.
   *
   * ⚠️ `wybrana` ROZSTRZYGA „WYBRANO PUSTE" OD „NIC NIE WYBRANO" — `wybrany === null` znaczy
   * to drugie i nie wolno tych stanów zlewać: jeden pozwala zlecić operację, drugi nie.
   */
  pozycjaPusta?: { etykieta: string; wybrana: boolean; onWybierz: () => void };
}) {
  const { t } = useI18n();
  const [otwarty, setOtwarty] = useState(false);

  return (
    <div className="relative flex flex-col gap-1 text-xs text-muted-foreground">
      {t('planogram.item')}
      <div className="flex items-center gap-1">
        <Input
          value={
            otwarty
              ? szukaj
              : /** ⚠️ Wybrana pozycja pusta ma się pokazać W POLU — puste pole znaczy „nic nie wybrano". */
                (pozycjaPusta?.wybrana === true ? pozycjaPusta.etykieta : (wybrany?.name ?? ''))
          }
          onChange={(e) => {
            onSzukaj(e.target.value);
            setOtwarty(true);
          }}
          onFocus={() => setOtwarty(true)}
          placeholder={t('planogram.searchItem')}
          className="h-9 flex-1"
        />
        {(wybrany !== null || pozycjaPusta?.wybrana === true) && (
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('planogram.clearItem')}
            onClick={() => {
              onWybierz(null);
              onSzukaj('');
            }}
          >
            <X />
          </Button>
        )}
      </div>

      {otwarty && (
        <>
          {/* klik poza listą ją zamyka — bez tego lista zasłania panel do końca sesji */}
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-30 cursor-default"
            onClick={() => setOtwarty(false)}
          />
          <ul className="absolute top-full z-40 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-lg">
            {/*
              ⚠️ POZYCJA PUSTA NAD WSZYSTKIM, TAKŻE NAD „wczytuję" i „nic nie znaleziono" —
              zamówiono ją jako PIERWSZĄ rzecz na liście, a jej wybór nie zależy od tego, czy
              szukanie towaru cokolwiek zwróciło.
            */}
            {pozycjaPusta !== undefined && (
              <li>
                <button
                  type="button"
                  onClick={() => {
                    pozycjaPusta.onWybierz();
                    setOtwarty(false);
                  }}
                  className="flex w-full rounded px-2 py-1.5 text-left italic text-muted-foreground hover:bg-accent"
                >
                  {pozycjaPusta.etykieta}
                </button>
              </li>
            )}
            {ladowanie && <li className="px-2 py-1.5 text-muted-foreground">{t('common.loading')}</li>}
            {!ladowanie && wyniki.length === 0 && (
              <li className="px-2 py-1.5 text-muted-foreground">{t('planogram.noItemsFound')}</li>
            )}
            {wyniki.map((w) => (
              <li key={w.id}>
                <button
                  type="button"
                  onClick={() => {
                    onWybierz(w);
                    setOtwarty(false);
                  }}
                  className="flex w-full flex-col rounded px-2 py-1.5 text-left hover:bg-accent"
                >
                  <span className="truncate text-foreground">{w.name}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{w.itemNumber}</span>
                </button>
              </li>
            ))}

            {/*
              LICZNIK NA DOLE — ZAWSZE, nie tylko przy ucięciu.
              ⚠️ Przewinięcie listy do końca to jedyny sposób, jakim człowiek sprawdza, czy czegoś
              nie ma. „pokazano 25 z 25" mówi mu, że sprawdził CAŁOŚĆ; brak licznika kazał mu to
              zgadywać i przy 20 z 25 zgadywał źle (zgłoszone 26.08.2026).
              ⚠️ `sticky`, bo lista ma `max-h-64` i licznik na końcu treści byłby poza widokiem
              dokładnie tak długo, jak długo jest potrzebny.
            */}
            {!ladowanie && wyniki.length > 0 && (
              <li className="sticky bottom-0 border-t border-border bg-popover px-2 pb-0.5 pt-1 text-[10px] text-muted-foreground">
                {t('planogram.itemsShown').replace('{n}', String(wyniki.length)).replace('{total}', String(razem))}
                {podsumowanieListy(wyniki.length, razem).ucieta && (
                  <span className="text-amber-600 dark:text-amber-400"> · {t('planogram.itemsNarrow')}</span>
                )}
              </li>
            )}
          </ul>
        </>
      )}
    </div>
  );
}
